import React, { useState, createRef } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import Drawer from '@material-ui/core/Drawer';
import TextField from 'components/TextField';
import Button from 'components/Button';
import COLORS from 'shared/constants/colors';
import { HexColorPicker } from 'react-colorful';
import FontPicker from 'font-picker-react';
import Slider from '@material-ui/core/Slider';
import { default as MUIButton } from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import FormatBoldIcon from '@material-ui/icons/FormatBold';
import FormatItalicIcon from '@material-ui/icons/FormatItalic';
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined';
import FormatStrikethroughIcon from '@material-ui/icons/FormatStrikethrough';
import VerticalAlignTopIcon from '@material-ui/icons/VerticalAlignTop';
import {
  Container,
  Header,
  CloseIconStyle,
  FontPickerWrapper,
  HexColorPickerWrapper,
} from 'styles/components/ArtWork/browseLibrary';
import html2canvas from 'html2canvas';

const valueText = (value) => value;

const BrowseTextDrawer = ({ open, toggle, onDrop }) => {
  const [text, setText] = useState(null);
  const [color, setColor] = useState('#000000');
  const [fontFamily, setFontFamily] = useState('Open Sans');
  const [fontSize, setFontSize] = useState(20);
  const [fontBold, setFontBold] = useState(undefined);
  const [fontItalic, setFontItalic] = useState(undefined);
  const [fontUnderline, setFontUnderline] = useState(undefined);
  const [fontStrikeThrough, setFontStrikeThrough] = useState(undefined);
  const [fontOverline, setFontOverline] = useState(undefined);
  const previewTextRef = createRef(null);

  return (
    <Drawer anchor='right' open={open} onClose={toggle}>
      <Container style={{ width: '30vw' }}>
        <Header style={{ borderBottom: '1px solid #ccc' }}>
          <h2>Add Text</h2>
          <CloseIconStyle onClick={toggle}></CloseIconStyle>
        </Header>
        <div style={{ textAlign: 'center', margin: '30px 0' }}>
          <div ref={previewTextRef} style={{ display: 'inline-block' }}>
            <h1
              className='apply-font'
              style={{
                textAlign: 'center',
                margin: 0,
                padding: 0,
                color: color || '#000',
                fontSize,
                display: 'inline-block',
                fontWeight: fontBold ? 'bold' : 'normal',
                textDecoration: `${fontUnderline ? 'underline' : ''} ${
                  fontStrikeThrough ? 'line-through' : ''
                } ${fontOverline ? 'overline' : ''}`,
                fontStyle: fontItalic ? 'italic' : 'unset',
              }}>
              {text?.length ? text : 'Enter text to preview'}
            </h1>
          </div>
        </div>

        <TextField
          label={`Enter Text (${text?.length ? text?.length : 0}/20)`}
          style={{ width: '100%' }}
          value={text || ''}
          onChange={(v) => {
            if (v?.length <= 20) {
              setText(v);
            }
          }}
        />

        {text && (
          <div style={{ margin: 20, textAlign: 'center' }}>
            <ButtonGroup
              color='primary'
              aria-label='outlined primary button group'>
              <MUIButton
                onClick={() => setFontBold(!fontBold)}
                variant={fontBold ? 'contained' : 'outlined'}>
                <FormatBoldIcon />
              </MUIButton>
              <MUIButton
                onClick={() => setFontItalic(!fontItalic)}
                variant={fontItalic ? 'contained' : 'outlined'}>
                <FormatItalicIcon />
              </MUIButton>
              <MUIButton
                onClick={() => setFontUnderline(!fontUnderline)}
                variant={fontUnderline ? 'contained' : 'outlined'}>
                <FormatUnderlinedIcon />
              </MUIButton>
              <MUIButton
                onClick={() => setFontStrikeThrough(!fontStrikeThrough)}
                variant={fontStrikeThrough ? 'contained' : 'outlined'}>
                <FormatStrikethroughIcon />
              </MUIButton>
              <MUIButton
                onClick={() => setFontOverline(!fontOverline)}
                variant={fontOverline ? 'contained' : 'outlined'}>
                <VerticalAlignTopIcon />
              </MUIButton>
            </ButtonGroup>
          </div>
        )}

        {text && (
          <div style={{ margin: '30px 0' }}>
            <h3 style={{ borderBottom: '1px solid #ccc', paddingBottom: 10 }}>
              Select Font Family
            </h3>
            <FontPickerWrapper>
              <FontPicker
                apiKey='AIzaSyCsHLrd2g7rOdgIM56xluJCk88wNzEup5Y'
                activeFontFamily={fontFamily}
                onChange={(nextFont) => setFontFamily(nextFont.family)}
              />
            </FontPickerWrapper>
          </div>
        )}

        {text && (
          <div style={{ margin: '30px 0' }}>
            <h3 style={{ borderBottom: '1px solid #ccc', paddingBottom: 10 }}>
              Select Font Size ({`${fontSize}px`})
            </h3>
            <div style={{ width: '100%', padding: '30px 10px 0 10px' }}>
              <Slider
                defaultValue={10}
                getAriaValueText={valueText}
                step={2}
                valueLabelDisplay='on'
                marks
                min={20}
                max={70}
                value={fontSize}
                onChange={(e, v) => setFontSize(v)}
              />
            </div>
          </div>
        )}

        {text && (
          <div style={{ margin: '30px 0' }}>
            <h3 style={{ borderBottom: '1px solid #ccc', paddingBottom: 10 }}>
              Select Text Color ({color})
            </h3>
            <HexColorPickerWrapper>
              <HexColorPicker
                color={color}
                onChange={(color) => {
                  setColor(color);
                }}
              />
            </HexColorPickerWrapper>
          </div>
        )}

        {text && (
          <div style={{ textAlign: 'center' }}>
            <Button
              containerStyle={{
                backgroundColor: COLORS.DARK_BLUE,
                color: COLORS.WHITE,
                fontWeight: 'normal',
                boxShadow: '4px 4px 20px rgba(0, 0, 0, 0.05)',
                textTransform: 'none',
                borderRadius: '3px',
                fontSize: '16px',
                lineHeight: '1.5',
                marginTop: 30,
                width: '100%',
              }}
              title='Add Text'
              onClick={() => {
                const width =
                  previewTextRef?.current?.clientWidth + (fontItalic ? 12 : 0);
                const height = previewTextRef?.current?.clientHeight;

                html2canvas(previewTextRef?.current, {
                  scrollX: 0,
                  scrollY: 0,
                  width,
                  height,
                  backgroundColor: 'transparent',
                }).then(function (canvas) {
                  onDrop({
                    secure_url: canvas.toDataURL(),
                    width,
                    height,
                    public_id: `${moment().millisecond()}${text}`,
                  });
                });
              }}
            />
          </div>
        )}
      </Container>
    </Drawer>
  );
};

BrowseTextDrawer.propTypes = {
  open: PropTypes.bool,
  toggle: PropTypes.func,
};

export default BrowseTextDrawer;
